/**
 * Small helpers shared between Mail Intake views. Lives outside of the
 * view components so tests can exercise the normalization logic without
 * booting Apryse / WebViewer / Redux.
 */

/**
 * Auto-match confidence can arrive in two shapes depending on whether we
 * hit the real backend (`auto_match_confidence`, 0..1 float on the detail
 * and list serializers) or the MSW mock (`confidence` nested on
 * `auto_match`, currently emitted as a 0..100 integer). Normalize both to
 * the 0..1 scale the shared `ConfidencePill` expects so callers don't
 * duplicate the scaling logic.
 *
 * Returns `null` for missing / non-numeric / negative inputs so the pill
 * renders nothing on bad data instead of throwing or misclassifying.
 *
 * Scale inference: values strictly greater than `SCALE_THRESHOLD` (1.5)
 * are treated as 0..100 and divided by 100. Values at or below the
 * threshold are assumed to be on the 0..1 scale and clamped to [0, 1],
 * so floating-point noise slightly above 1.0 (e.g. `1.005` from the
 * real backend) stays at 100% instead of collapsing to 1%.
 */
const SCALE_THRESHOLD = 1.5;

export const normalizeConfidence = (raw) => {
  if (raw == null) return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  if (n < 0) return null;
  if (n > SCALE_THRESHOLD) return Math.min(n / 100, 1);
  return Math.min(n, 1);
};

/**
 * Choose the viewer source for an intake item.
 *
 * Preference order (firehose day one, 2026-07-09):
 *
 * 1. `view_url` — a short-lived presigned S3 GET URL minted by the
 *    backend against the object's REAL bucket. Mail-connector items'
 *    PDFs live in the mail system's bucket (document-hub-arxiv), which
 *    the document streaming service cannot serve; every intake item has
 *    a staging `document` from the moment Phase A starts, so preferring
 *    the document id sent every intake PDF to a dead stream URL and
 *    left the sorter viewer blank.
 * 2. `document` (staging/assigned document id) — streaming-service
 *    fallback for items with no pre-assignment view_url.
 *
 * Returns `{ kind: 'presigned' | 'stream', value }` or `null` when the
 * item has neither source.
 */
export const pickViewerSource = (item) => {
  if (!item) return null;
  if (item.view_url) return { kind: "presigned", value: item.view_url };
  if (item.document) return { kind: "stream", value: item.document };
  return null;
};

/**
 * Describe a failed intake item so the Assign screen can say what is
 * wrong instead of leaving the viewer spinning (Matthew, 2026-10-08: a
 * zero-byte "error page saved as .pdf" from July sat at the front of the
 * queue and the screen gave no hint why nothing was loading).
 *
 * The backend writes a plain-English message with the pipeline's error
 * code in a trailing parenthetical, e.g.
 * "This is not actually a PDF — ... (file.not_pdf)". Codes in the
 * `file.` and `fetch.` families mean the bytes themselves are unusable,
 * so there is nothing for the PDF viewer to render; other families
 * (`extract.`, `reconciler.`, ...) mean the AI could not read it but the
 * PDF may still open for a human.
 *
 * Returns `null` unless `intake_pipeline_status` is "failed".
 */
const UNUSABLE_FILE_CODE_PREFIXES = ["file.", "fetch."];

export const describeIntakeFailure = (item) => {
  if (!item || item.intake_pipeline_status !== "failed") return null;
  const raw = (item.intake_pipeline_error || "").trim();
  const codeMatch = raw.match(/\(([a-z0-9_]+(?:\.[a-z0-9_]+)+)\)\s*$/i);
  const code = codeMatch ? codeMatch[1].toLowerCase() : "";
  const message = codeMatch ? raw.slice(0, codeMatch.index).trim() : raw;
  const fileUnusable =
    UNUSABLE_FILE_CODE_PREFIXES.some((p) => code.startsWith(p)) ||
    (!code && Number(item.file_size) === 0);
  return {
    code,
    fileUnusable,
    title: fileUnusable
      ? "This file can't be opened"
      : "The AI couldn't read this file",
    detail:
      message ||
      (fileUnusable
        ? "The upload is empty or isn't a real PDF."
        : "Text extraction failed; the PDF may still open on the right."),
    suggestion: fileUnusable
      ? "Delete it here, then find and upload the real document again."
      : "If the pages look fine, assign it as usual; otherwise delete it and re-scan.",
  };
};

/**
 * Normalize the backend's current flat auto-match fields into the same
 * Matter object shape the search-result picker stores in AssignView.
 *
 * Current API:
 *   auto_match_matter: "a0L..."
 *   auto_match_matter_name: "Kevin Seger | 4305 | VA Agency"
 *   auto_match_confidence: 0.965
 *
 * Older mocks used `auto_match: {type, matters}`. Keep that fallback
 * for stories/tests, but the flat production contract wins.
 */
export const pickAutoMatchMatter = (item) => {
  if (!item) return null;
  if (item.auto_match_matter) {
    const displayName =
      item.auto_match_matter_name || String(item.auto_match_matter);
    return {
      id: item.auto_match_matter,
      display_name: displayName,
      client_name: displayName,
      auto_match_confidence: item.auto_match_confidence ?? null,
      auto_match_method: item.auto_match_method || "",
      is_auto_match: true,
    };
  }
  const legacy = item.auto_match;
  if (legacy?.type === "single_matter" && legacy.matters?.[0]) {
    return legacy.matters[0];
  }
  return null;
};

/**
 * Translate the matcher's machine method string into short, sorter-
 * friendly evidence labels.
 *
 * Methods look like "deterministic_ladder_v1:ssn_last_four+name_only"
 * or "ssn_last_four+name_strong+contextual_llm_v1" (candidate-level).
 * Tokens the sorter doesn't need (ladder version markers) are dropped;
 * unknown tokens fall through Title-Cased so a future matcher signal
 * still renders something sensible instead of nothing.
 */
const METHOD_LABELS = {
  va_file_number: "VA file number",
  ssn_last_four: "SSN last 4",
  party_contact: "Phone / email / address",
  name_strong: "Strong name match",
  name_medium: "Partial name match",
  name_weak: "Weak name match",
  name_only: "Name match",
  contextual_llm_v1: "AI content comparison",
};

const METHOD_IGNORED = new Set([
  "deterministic_ladder_v1",
  "empty_identity",
  "contextual_unavailable",
]);

const GENERIC_CONTACT_LABEL = METHOD_LABELS.party_contact;

/**
 * The matcher's method string only records that CONTACT evidence
 * matched (`party_contact`), not WHICH identifier. The specific
 * identifier is stated in the operator-visible reason line ("Exact
 * phone ending 1212 + strong name similarity ..."), so parse it back
 * out to show one chip per matching identifier — name, SSN, address,
 * phone, email, city/state, date of birth.
 */
const CONTACT_SIGNAL_PATTERNS = [
  [/\bexact email\b/i, "Email match"],
  [/\bexact phone\b/i, "Phone match"],
  [/\bexact (?:mailing )?address\b/i, "Address match"],
  [/\bexact city\/state\b/i, "City/state match"],
  [/\bexact date of birth\b/i, "Date of birth match"],
];

export const extractContactSignals = (reason) => {
  const text = String(reason || "");
  return CONTACT_SIGNAL_PATTERNS.filter(([re]) => re.test(text)).map(
    ([, label]) => label,
  );
};

export const explainMatchMethod = (method, reason) => {
  if (!method) return [];
  const labels = [];
  for (const token of String(method).split(/[+:]/)) {
    const key = token.trim();
    if (!key || METHOD_IGNORED.has(key)) continue;
    let tokenLabels;
    if (key === "party_contact") {
      const specific = extractContactSignals(reason);
      tokenLabels = specific.length > 0 ? specific : [GENERIC_CONTACT_LABEL];
    } else {
      tokenLabels = [
        METHOD_LABELS[key] ||
          key.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase()),
      ];
    }
    for (const label of tokenLabels) {
      if (!labels.includes(label)) labels.push(label);
    }
  }
  return labels;
};

/**
 * Build the "why did the AI pick this matter?" panel data from the
 * detail serializer's resolved candidate list.
 *
 * Returns null when there is nothing explanatory to show (no
 * candidates at all). Otherwise:
 *
 *   {
 *     primary: { matterName, confidence, signals[], reasons[] } | null,
 *     alternates: [{ matterName, confidence, reasons[] }, ...]  // ≤3
 *   }
 *
 * `primary` is the candidate matching the current auto-match
 * suggestion; `alternates` are the other ranked candidates so the
 * sorter can see what else was considered. Reason strings come from
 * the matcher's operator-visible audit trail and are split on "; "
 * into separate lines for readability.
 */
export const buildSuggestionExplanation = (item) => {
  const candidates = Array.isArray(item?.match_candidates_resolved)
    ? item.match_candidates_resolved.filter(
        (c) => c && typeof c === "object",
      )
    : [];
  if (candidates.length === 0) return null;

  const splitReasons = (reason) =>
    String(reason || "")
      .split("; ")
      .map((part) => part.trim())
      .filter(Boolean);

  const suggestionId =
    item.auto_match_matter != null ? String(item.auto_match_matter) : null;
  const primaryRaw = suggestionId
    ? candidates.find((c) => String(c.matter_id) === suggestionId)
    : null;

  const toAlternate = (c) => ({
    matterName: c.matter_name || String(c.matter_id),
    confidence: normalizeConfidence(c.confidence),
    reasons: splitReasons(c.reason),
  });

  const alternates = candidates
    .filter((c) => c !== primaryRaw)
    .sort(
      (a, b) =>
        (normalizeConfidence(b.confidence) ?? 0) -
        (normalizeConfidence(a.confidence) ?? 0),
    )
    .slice(0, 3)
    .map(toAlternate);

  return {
    primary: primaryRaw
      ? {
          matterName:
            primaryRaw.matter_name ||
            item.auto_match_matter_name ||
            String(primaryRaw.matter_id),
          confidence: normalizeConfidence(
            primaryRaw.confidence ?? item.auto_match_confidence,
          ),
          signals: explainMatchMethod(primaryRaw.method, primaryRaw.reason),
          reasons: splitReasons(primaryRaw.reason),
        }
      : null,
    alternates,
  };
};

export const reconcileAutoMatch = ({
  existingMatter,
  suggestion,
  dismissed,
}) => {
  // Backend withdrew the suggestion: clear only a stale AI-selected
  // card. A human search pick is independent and must remain.
  if (!suggestion) {
    return existingMatter?.is_auto_match ? null : existingMatter;
  }
  // Explicit rejection of the AI card persists for this document.
  if (dismissed) return existingMatter;
  // Fill a blank field, or refresh an existing AI card when backend
  // confidence/name/matter changes. Never overwrite a manual pick.
  if (!existingMatter || existingMatter.is_auto_match) return suggestion;
  return existingMatter;
};
